import React, { createContext, useContext, useMemo } from "react";
import { useBrandName } from "@/hooks/useBrandName";
import { BrandContext, BrandName } from "@/types/branding";
import {
  getBrandContactUrl,
  getBrandContributionUrl,
  getBrandCoreWebsiteUrl,
  getBrandEmail,
  getBrandFavicon,
  getBrandPrivacyPolicyUrl,
  getBrandReviewUrl,
  getBrandTelephone,
  getBrandTermsAndConditionsUrl,
  getBrandTrustPilotUrl,
  getBrandWhatsApp,
  getCookiesPolicyUrl,
  getIntestacyRulesUrl,
} from "@/helpers/branding-helpers";
import { SettldContactInfo } from "@/utils/Constants";

const BrandContextProvider = createContext<BrandContext>({
  brand: BrandName.SETTLD,
  email: SettldContactInfo.Email,
  tel: SettldContactInfo.Telephone,
  contactUsUrl: SettldContactInfo.ContactUs,
  termsAndConditionsUrl: SettldContactInfo.TermsAndConditionsUrl,
  privacyPolicyUrl: SettldContactInfo.PrivacyPolicyUrl,
  reviewUrl: SettldContactInfo.ReviewUrl,
  trustPilotUrl: SettldContactInfo.TrustPilotUrl,
  contributionUrl: SettldContactInfo.ContributionUrl,
  coreWebsiteUrl: SettldContactInfo.CoreWebsiteUrl,
  intestacyRulesUrl: SettldContactInfo.IntestacyRulesUrl,
  cookiePolicyUrl: SettldContactInfo.CookiePolicyUrl,
  whatsApp: SettldContactInfo.WhatsApp,
});

export const BrandNameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const brandName = useBrandName();
  React.useEffect(() => {
    const favicon = document.getElementById("dynamic-favicon") as HTMLLinkElement;
    if (favicon) {
      favicon.href = getBrandFavicon(brandName);
    }
    window.document.title = brandName;
  }, [brandName]);
  const memoizedValue = useMemo(
    () => ({
      brand: brandName,
      email: getBrandEmail(brandName),
      tel: getBrandTelephone(brandName),
      contactUsUrl: getBrandContactUrl(brandName),
      termsAndConditionsUrl: getBrandTermsAndConditionsUrl(brandName),
      privacyPolicyUrl: getBrandPrivacyPolicyUrl(brandName),
      reviewUrl: getBrandReviewUrl(brandName),
      trustPilotUrl: getBrandTrustPilotUrl(brandName),
      contributionUrl: getBrandContributionUrl(brandName),
      coreWebsiteUrl: getBrandCoreWebsiteUrl(brandName),
      intestacyRulesUrl: getIntestacyRulesUrl(brandName),
      cookiePolicyUrl: getCookiesPolicyUrl(brandName),
      whatsApp: getBrandWhatsApp(brandName),
    }),
    [brandName]
  );

  return (
    <BrandContextProvider.Provider value={memoizedValue}>{children}</BrandContextProvider.Provider>
  );
};

export const useBrandingContext = (): BrandContext => {
  const context = useContext(BrandContextProvider);

  if (context === undefined) {
    throw new Error("useBrandNameContext must be used within a BrandNameProvider");
  }

  return context;
};
